import * as React from "react"
import { cva, type VariantProps } from "class-variance-authority"
import { useFormContext, FieldValues, FieldPath } from "react-hook-form"
import { Button } from "@/components/ui/button"

// Badge variants
export const badgeVariants = cva(
  "inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-semibold transition-colors focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2",
  {
    variants: {
      variant: {
        default:
          "border-transparent bg-primary text-primary-foreground hover:bg-primary/80",
        secondary:
          "border-transparent bg-secondary text-secondary-foreground hover:bg-secondary/80",
        destructive:
          "border-transparent bg-destructive text-destructive-foreground hover:bg-destructive/80",
        outline: "text-foreground",
      },
    },
    defaultVariants: {
      variant: "default",
    },
  }
)

export type BadgeProps = React.HTMLAttributes<HTMLDivElement> & VariantProps<typeof badgeVariants>

// Button variants
export const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0",
  {
    variants: {
      variant: {
        default: "bg-primary text-primary-foreground hover:bg-primary/90",
        destructive:
          "bg-destructive text-destructive-foreground hover:bg-destructive/90",
        outline:
          "border border-input bg-background hover:bg-accent hover:text-accent-foreground",
        secondary:
          "bg-secondary text-secondary-foreground hover:bg-secondary/80",
        ghost: "hover:bg-accent hover:text-accent-foreground",
        link: "text-primary underline-offset-4 hover:underline",
      },
      size: {
        default: "h-10 px-4 py-2",
        sm: "h-9 rounded-md px-3",
        lg: "h-11 rounded-md px-8",
        icon: "h-10 w-10",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
)

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean
}

// Toggle variants
export const toggleVariants = cva(
  "inline-flex items-center justify-center rounded-md text-sm font-medium ring-offset-background transition-colors hover:bg-muted hover:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 data-[state=on]:bg-accent data-[state=on]:text-accent-foreground",
  {
    variants: {
      variant: {
        default: "bg-transparent",
        outline:
          "border border-input bg-transparent hover:bg-accent hover:text-accent-foreground",
      },
      size: {
        default: "h-10 px-3",
        sm: "h-9 px-2.5",
        lg: "h-11 px-5",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
)

export type ToggleProps = React.ComponentPropsWithoutRef<typeof React.forwardRef> & VariantProps<typeof toggleVariants>

// Navigation menu constants and types
export const navigationMenuTriggerStyle = () =>
  cva(
    "group inline-flex h-10 w-max items-center justify-center rounded-md bg-background px-4 py-2 text-sm font-medium transition-colors hover:bg-accent hover:text-accent-foreground focus:bg-accent focus:text-accent-foreground focus:outline-none disabled:pointer-events-none disabled:opacity-50 data-[active]:bg-accent/50 data-[state=open]:bg-accent/50"
  )

// Form contexts and utility functions
type FormFieldContextValue<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>
> = {
  name: TName
}

type FormItemContextValue = {
  id: string
}

export const FormFieldContext = React.createContext<FormFieldContextValue>(
  {} as FormFieldContextValue
)

export const FormItemContext = React.createContext<FormItemContextValue>(
  {} as FormItemContextValue
)

export const useFormField = () => {
  const fieldContext = React.useContext(FormFieldContext)
  const itemContext = React.useContext(FormItemContext)
  const { getFieldState, formState } = useFormContext()

  const fieldState = getFieldState(fieldContext.name, formState)

  if (!fieldContext) {
    throw new Error("useFormField should be used within <FormField>")
  }

  const { id } = itemContext

  return {
    id,
    name: fieldContext.name,
    formItemId: `${id}-form-item`,
    formDescriptionId: `${id}-form-item-description`,
    formMessageId: `${id}-form-item-message`,
    ...fieldState,
  }
}

// Widget configurations
export const WIDGET_CONFIGURATIONS = {
  revenue: {
    gridLayout: { w: 6, h: 8, minW: 4, minH: 6 },
    title: 'Revenue Analytics',
    description: 'Track revenue trends and projections'
  },
  pipeline: {
    gridLayout: { w: 6, h: 8, minW: 4, minH: 6 },
    title: 'Sales Pipeline',
    description: 'Monitor your sales pipeline progress'
  },
  activity: {
    gridLayout: { w: 6, h: 6, minW: 4, minH: 4 },
    title: 'Recent Activity',
    description: 'Latest activities and interactions'
  },
  tasks: {
    gridLayout: { w: 6, h: 6, minW: 4, minH: 4 },
    title: 'Upcoming Tasks',
    description: 'Your pending tasks and deadlines'
  },
  performance: {
    gridLayout: { w: 12, h: 8, minW: 6, minH: 6 },
    title: 'Performance Metrics',
    description: 'Key performance indicators'
  },
  client_distribution: {
    gridLayout: { w: 6, h: 8, minW: 4, minH: 6 },
    title: 'Client Distribution',
    description: 'Geographic distribution of clients'
  },
  financial: {
    gridLayout: { w: 6, h: 8, minW: 4, minH: 6 },
    title: 'Financial Overview',
    description: 'Revenue and expense tracking'
  },
  opportunities: {
    gridLayout: { w: 6, h: 6, minW: 4, minH: 4 },
    title: 'Opportunities',
    description: 'Sales opportunities pipeline'
  },
  engagement: {
    gridLayout: { w: 6, h: 6, minW: 4, minH: 4 },
    title: 'Client Engagement',
    description: 'Client interaction metrics'
  }
}

export const WIDGET_ERROR_MESSAGES = {
  generic: 'An error occurred while loading this widget',
  network: 'Network error - please check your connection',
  unauthorized: 'You do not have permission to view this data',
  notFound: 'The requested data could not be found',
  server: 'Server error - please try again later'
}

// Widget renderers - moved here to avoid Fast Refresh issues
export const WidgetRenderers: Record<string, React.FC<any>> = {
  // Metric widgets
  PipelineSummary: ({ data, options = {} }) => {
    const { totalPipelineValue = 0, opportunityCount = 0 } = data || {};
    // Ensure we have numeric values
    const numericPipelineValue = typeof totalPipelineValue === 'string' ? parseFloat(totalPipelineValue) : totalPipelineValue;
    const numericOpportunityCount = typeof opportunityCount === 'string' ? parseInt(opportunityCount) : opportunityCount;
    
    // Calculate average with validation
    const avgDealSize = numericOpportunityCount > 0 ? numericPipelineValue / numericOpportunityCount : 0;
    
    return (
      <div className="text-center">
        <div className="text-3xl font-bold">
          ${(numericPipelineValue || 0).toLocaleString()}
        </div>
        <div className="text-sm text-muted-foreground">
          {numericOpportunityCount || 0} opportunities
        </div>
        {options.showAvgDealSize && (
          <div className="mt-2 text-sm">
            <span className="font-medium">Avg Deal Size:</span>{" "}
            ${avgDealSize.toLocaleString()}
          </div>
        )}
      </div>
    );
  },
  
  RelationshipHealth: ({ data, options = {} }) => {
    const {
      averageHealthScore = 0,
      totalRelationshipsAnalyzed = 0,
      healthyRelationships = 0,
      atRiskRelationships = 0,
    } = data || {};
    
    // Ensure we have numeric values
    const numericAverageHealthScore = typeof averageHealthScore === 'string' ? parseFloat(averageHealthScore) : averageHealthScore;
    const numericTotalRelationships = typeof totalRelationshipsAnalyzed === 'string' ? parseInt(totalRelationshipsAnalyzed) : totalRelationshipsAnalyzed;
    const numericHealthyRelationships = typeof healthyRelationships === 'string' ? parseInt(healthyRelationships) : healthyRelationships;
    const numericAtRiskRelationships = typeof atRiskRelationships === 'string' ? parseInt(atRiskRelationships) : atRiskRelationships;
    
    return (
      <div className="text-center">
        <div className="text-3xl font-bold">
          {numericAverageHealthScore ? numericAverageHealthScore.toFixed(1) : "N/A"}
        </div>
        <div className="text-sm text-muted-foreground">
          Average score across {numericTotalRelationships || 0} relationships
        </div>
        {options.showDetail && (
          <div className="mt-2 text-sm">
            <div className="flex justify-center gap-4">
              <span className="text-green-600">
                {numericHealthyRelationships || 0} Healthy
              </span>
              <span className="text-red-600">
                {numericAtRiskRelationships || 0} At Risk
              </span>
            </div>
          </div>
        )}
      </div>
    );
  },

  UpcomingTasks: ({ data, options = {} }) => {
    const { tasks = [], totalCount = 0 } = data || {};
    const limit = typeof options.limit === 'string' ? parseInt(options.limit) : (options.limit || 5);
    const daysAhead = typeof options.daysAhead === 'string' ? parseInt(options.daysAhead) : (options.daysAhead || 7);
    
    // Ensure we have numeric values
    const numericTotalCount = typeof totalCount === 'string' ? parseInt(totalCount) : totalCount;
    
    return (
      <div className="text-center">
        <div className="text-3xl font-bold">{numericTotalCount || 0}</div>
        <div className="text-sm text-muted-foreground">
          Due in the next {daysAhead} days
        </div>
        <div className="mt-2">
          <Button variant="outline" size="sm">View Tasks</Button>
        </div>
      </div>
    );
  },

  // Chart widgets
  PipelineChart: ({ data, options = {} }) => {
    return (
      <div>
        {/* This would be a real chart in production */}
        <div className="h-[200px] bg-muted/30 flex items-center justify-center rounded-md">
          Pipeline chart visualization
        </div>
      </div>
    );
  },

  RevenueForecast: ({ data, options = {} }) => {
    return (
      <div>
        {/* This would be a real chart in production */}
        <div className="h-[200px] bg-muted/30 flex items-center justify-center rounded-md">
          Revenue forecast visualization
        </div>
      </div>
    );
  },

  ActivityTrend: ({ data, options = {} }) => {
    return (
      <div>
        {/* This would be a real chart in production */}
        <div className="h-[200px] bg-muted/30 flex items-center justify-center rounded-md">
          Activity trend visualization
        </div>
      </div>
    );
  },

  GrowthByRevenue: ({ data, options = {} }) => {
    return (
      <div>
        {/* This would be a real chart in production */}
        <div className="h-[200px] bg-muted/30 flex items-center justify-center rounded-md">
          Growth by revenue visualization
        </div>
      </div>
    );
  },

  // Status widgets
  AccountPlanStatus: ({ data, options = {} }) => {
    const { complete = 0, total = 0, quarter = "Q2 2025" } = data || {};
    
    // Ensure we have numeric values
    const numericComplete = typeof complete === 'string' ? parseInt(complete) : complete;
    const numericTotal = typeof total === 'string' ? parseInt(total) : total;
    
    // Calculate percentage safely
    const percentage = numericTotal > 0 ? Math.min(100, Math.max(0, Math.round((numericComplete / numericTotal) * 100))) : 0;
    
    return (
      <div className="space-y-4">
        <div className="flex justify-between text-sm">
          <span>{quarter} Plans</span>
          <span className="font-medium">{numericComplete}/{numericTotal} Complete</span>
        </div>
        <div className="h-2 bg-muted rounded-full overflow-hidden">
          <div 
            className="h-full bg-primary" 
            style={{ width: `${percentage}%` }}
          ></div>
        </div>
      </div>
    );
  },

  // Stats widgets
  GiftProgram: ({ data, options = {} }) => {
    const {
      sentCount = 0,
      pendingCount = 0,
      budgetRemaining = 0,
    } = data || {};
    
    // Ensure we have numeric values
    const numericSentCount = typeof sentCount === 'string' ? parseInt(sentCount) : sentCount;
    const numericPendingCount = typeof pendingCount === 'string' ? parseInt(pendingCount) : pendingCount;
    const numericBudgetRemaining = typeof budgetRemaining === 'string' ? parseFloat(budgetRemaining) : budgetRemaining;
    
    return (
      <div className="space-y-2">
        <div className="flex justify-between">
          <span className="text-sm">Gifts Sent This Year</span>
          <span className="font-medium">{numericSentCount}</span>
        </div>
        <div className="flex justify-between">
          <span className="text-sm">Pending Thank-Yous</span>
          <span className="font-medium">{numericPendingCount}</span>
        </div>
        <div className="flex justify-between">
          <span className="text-sm">Budget Remaining</span>
          <span className="font-medium">${(numericBudgetRemaining || 0).toLocaleString()}</span>
        </div>
      </div>
    );
  },

  LandExpand: ({ data, options = {} }) => {
    const {
      activeCount = 0,
      potentialValue = 0,
      growthYoY = 0,
    } = data || {};
    
    // Ensure we have numeric values
    const numericActiveCount = typeof activeCount === 'string' ? parseInt(activeCount) : activeCount;
    const numericPotentialValue = typeof potentialValue === 'string' ? parseFloat(potentialValue) : potentialValue;
    const numericGrowthYoY = typeof growthYoY === 'string' ? parseFloat(growthYoY) : growthYoY;
    
    return (
      <div className="space-y-2">
        <div className="flex justify-between">
          <span className="text-sm">Active Expansions</span>
          <span className="font-medium">{numericActiveCount}</span>
        </div>
        <div className="flex justify-between">
          <span className="text-sm">Potential Value</span>
          <span className="font-medium">${(numericPotentialValue || 0).toLocaleString()}</span>
        </div>
        <div className="flex justify-between">
          <span className="text-sm">Growth YoY</span>
          <span className={`font-medium ${numericGrowthYoY >= 0 ? 'text-green-600' : 'text-red-600'}`}>
            {numericGrowthYoY >= 0 ? '+' : ''}{numericGrowthYoY}%
          </span>
        </div>
      </div>
    );
  },

  // Financial widgets - these will be imported dynamically to avoid circular imports
  RevenueOverview: ({ data, options = {} }) => <div>Revenue Overview Widget</div>,
  ProjectProfitability: ({ data, options = {} }) => <div>Project Profitability Widget</div>,
  OutstandingInvoices: ({ data, options = {} }) => <div>Outstanding Invoices Widget</div>,
  BudgetVsActual: ({ data, options = {} }) => <div>Budget vs Actual Widget</div>,
  RevenueTrend: ({ data, options = {} }) => <div>Revenue Trend Widget</div>,
}

// Widget metadata utility function
export function getWidgetMetadata(widgetId: string) {
  const metadata: Record<string, { title: string; description: string }> = {
    pipeline_summary: {
      title: "Pipeline Summary",
      description: "Total pipeline value and opportunity count",
    },
    relationship_health: {
      title: "Relationship Health",
      description: "Overall client relationship status",
    },
    upcoming_tasks: {
      title: "Upcoming Tasks",
      description: "Tasks due soon",
    },
    pipeline_chart: {
      title: "Opportunity Pipeline",
      description: "Current pipeline by stage",
    },
    revenue_forecast: {
      title: "Revenue Forecast",
      description: "Projected revenue by quarter",
    },
    account_plan_status: {
      title: "Account Plan Status",
      description: "Quarterly planning progress",
    },
    gift_program: {
      title: "Gift Program",
      description: "Current gift campaign status",
    },
    land_expand: {
      title: "Land & Expand",
      description: "Growth opportunities",
    },
    activity_trend: {
      title: "Activity Trends",
      description: "Activity trends over time",
    },
    growth_by_revenue: {
      title: "Growth by Revenue Type",
      description: "Revenue growth by type",
    },
    // Financial widgets
    revenue_overview: {
      title: "Revenue Overview",
      description: "Monthly revenue tracking and growth",
    },
    project_profitability: {
      title: "Project Profitability",
      description: "Profit margins by project",
    },
    outstanding_invoices: {
      title: "Outstanding Invoices",
      description: "Unpaid invoices and overdue amounts",
    },
    budget_vs_actual: {
      title: "Budget vs Actual",
      description: "Project budget utilization",
    },
    revenue_trend: {
      title: "Revenue Trend",
      description: "Revenue trend over time",
    },
  };
  
  return metadata[widgetId] || { 
    title: widgetId.split('_').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' '),
    description: "Custom widget",
  };
}

// Helper to get CSS class based on widget size
export function getSizeClass(position: { w: number; h: number }) {
  if (position.w === 2 && position.h === 2) {
    return "col-span-2 row-span-2";
  } else if (position.w === 2) {
    return "col-span-2";
  } else if (position.h === 2) {
    return "row-span-2";
  }
  return "";
}